import { supabase } from '@/integrations/supabase/client';
import { Referral, ReferralStatus } from './referral/types';
import { logger } from '@/utils/logger';

const mapRow = (r: any): Referral => ({
  id: r.id,
  referrerId: r.referrer_user_id,
  referrerName: r.metadata?.referrer_name ?? '',
  referrerEmail: r.metadata?.referrer_email ?? '',
  refereeId: r.metadata?.referee_id ?? '',
  refereeName: r.referee_name ?? '',
  refereeEmail: r.referee_email ?? '',
  status: (r.status as ReferralStatus) ?? ReferralStatus.PENDING,
  points: Number(r.reward_amount ?? 0),
  createdAt: r.created_at,
  convertedAt: r.converted_at ?? undefined,
  name: r.referee_name ?? undefined,
  email: r.referee_email ?? undefined,
  date: r.created_at,
});

export const getUserPoints = async (userId: string): Promise<number> => {
  const { data, error } = await supabase
    .from('referrals')
    .select('reward_amount')
    .eq('referrer_user_id', userId);
  if (error) {
    logger.error('getUserPoints failed', error);
    return 0;
  }
  return (data ?? []).reduce((s: number, r: any) => s + Number(r.reward_amount ?? 0), 0);
};

export const updateUserPoints = async (userId: string, points: number): Promise<number> => {
  logger.info('updateUserPoints noop (points derive from referrals)', { userId, points });
  return points;
};

export const getUserReferrals = async (userId: string): Promise<Referral[]> => {
  const { data, error } = await supabase
    .from('referrals')
    .select('*')
    .eq('referrer_user_id', userId)
    .order('created_at', { ascending: false });
  if (error) {
    logger.error('getUserReferrals failed', error);
    return [];
  }
  return (data ?? []).map(mapRow);
};

export const sendReferralInvitation = async (
  data: { name: string; email: string; phone?: string },
  method: string,
): Promise<{ success: boolean; message?: string }> => {
  try {
    const { error } = await supabase.functions.invoke('send-email', {
      body: {
        type: 'referral_invitation',
        to: data.email,
        payload: { name: data.name, method },
      },
    });
    if (error) throw error;
    return { success: true, message: 'Referral sent successfully' };
  } catch (e: any) {
    logger.error('sendReferralInvitation failed', e);
    return { success: false, message: e?.message ?? 'Failed to send' };
  }
};

export const rejectReferral = async (referralId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('referrals')
    .update({ status: 'rejected' })
    .eq('id', referralId);
  if (error) {
    logger.error('rejectReferral failed', error);
    return false;
  }
  return true;
};

export const deleteUserReferralCodeByEmail = async (email: string): Promise<boolean> => {
  const { error } = await supabase.from('referrals').delete().eq('referee_email', email);
  if (error) {
    logger.error('deleteUserReferralCodeByEmail failed', error);
    return false;
  }
  return true;
};

export const getAllReferrals = async (): Promise<Referral[]> => {
  const { data, error } = await supabase
    .from('referrals')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(500);
  if (error) {
    logger.error('getAllReferrals failed', error);
    return [];
  }
  return (data ?? []).map(mapRow);
};

export const getUserReferralCode = async (userId: string): Promise<string> => {
  const { data, error } = await supabase
    .from('referrals')
    .select('referral_code')
    .eq('referrer_user_id', userId)
    .limit(1)
    .maybeSingle();
  if (error || !data) return `REF${userId.substring(0, 6).toUpperCase()}`;
  return data.referral_code;
};

export const sendReferralWelcomeEmail = async (user: {
  name: string;
  email: string;
  referralCode?: string;
}): Promise<boolean> => {
  try {
    const { error } = await supabase.functions.invoke('send-email', {
      body: {
        type: 'referral_welcome',
        to: user.email,
        payload: { name: user.name, referralCode: user.referralCode },
      },
    });
    if (error) throw error;
    return true;
  } catch (e) {
    logger.error('sendReferralWelcomeEmail failed', e);
    return false;
  }
};

export const generateActivationToken = (): string =>
  `tok_${crypto.randomUUID().replace(/-/g, '')}`;

export const checkBusinessReferralLimit = async (_businessId: string) => {
  return { limitReached: false, currentCount: 0, maxCount: 100, pendingAllowed: true };
};

export const activatePendingReferral = async (
  userId: string,
  _businessId: string,
): Promise<boolean> => {
  const { error } = await supabase
    .from('referrals')
    .update({ status: 'approved', converted_at: new Date().toISOString() })
    .eq('referrer_user_id', userId)
    .eq('status', 'pending');
  if (error) {
    logger.error('activatePendingReferral failed', error);
    return false;
  }
  return true;
};

export const getPendingReferralsCount = async (_businessId?: string): Promise<number> => {
  const { count, error } = await supabase
    .from('referrals')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'pending');
  if (error) return 0;
  return count ?? 0;
};

export const sendReferralActivationEmail = async (
  email: string,
  name: string,
  activationToken: string,
  referralCode: string,
): Promise<boolean> => {
  try {
    const { error } = await supabase.functions.invoke('send-email', {
      body: {
        type: 'referral_activation',
        to: email,
        payload: { name, activationToken, referralCode },
      },
    });
    if (error) throw error;
    return true;
  } catch (e) {
    logger.error('sendReferralActivationEmail failed', e);
    return false;
  }
};
